import { useEffect, useRef } from 'react';
import * as s from './LuminariumBackdrop.css';
import VERT from './shaders/cyber-grid.vert.glsl?raw';
import FRAG from './shaders/cyber-grid.frag.glsl?raw';
import { buildShaderDefines } from './camera';
import { MOBILE_MAX_WIDTH } from '@/shared/ui/theme/media';
import { hologramSceneStore } from './hologramSceneStore';
import { coveredFraction, WINDOW_SURFACE_SELECTOR } from './windowCoverage';

// 창이 배경을 이 비율 이상 덮으면 앰비언트 루프를 쉰다(마지막 프레임이 그대로 남는다).
// Why: 움직이는 배경은 위 유리 창의 backdrop-filter 를 매 프레임 다시 그리게 해 창 6장 아래
// GPU 메모리를 +288MB 더 잡는다. 포커스 창 유리(14% 틴트 + blur 10px) 아래로는 모션이 약 1/10
// 만 비치고(눈에 띄게 변한 픽셀 1.3% vs 드러난 배경 12.7%), 비포커스 창 아래는 0% 다 — 2026-09-18 실측.
const PAUSE_COVERAGE = 0.8;
const COVERAGE_CHECK_MS = 500;

// 좁은(세로) 뷰포트 판정 — 빔·기둥 루프를 절반으로 줄인다.
// Why 폭 상수 import: 리터럴을 새로 적으면 "CSS 는 모바일인데 JS 는 데스크톱" 사각지대가 생긴다.
// Why 로드 시점 1회: DEFINES/FRAG_V2/FRAG_V1 이 top-level const 라 회전(portrait↔landscape)에는
// 반응하지 않는다. 회전 대응은 createProgram 을 useEffect 로 내리고 narrow 변화 시 재컴파일해야
// 하므로 별도 범위다.
const NARROW = typeof window !== 'undefined' && window.innerWidth <= MOBILE_MAX_WIDTH;

// 카메라/모달 상수 single source = camera.ts. 컴파일 직전 #define 블록으로 주입.
const DEFINES = buildShaderDefines({ narrow: NARROW });

// WebGL2: #version 라인 바로 다음에 주입 (#version은 반드시 첫 라인이어야 함)
const FRAG_V2 = FRAG.replace('#version 300 es\n', `#version 300 es\n${DEFINES}`);

// WebGL1 fallbacks (no `in/out`, no `#version`, no `discard` in conditionals with derivatives)
const VERT_V1 = VERT.replace('#version 300 es\n', '')
  .replace(/\bin\b/g, 'attribute')
  .replace(/\bout\b/g, 'varying');

// Why: DEFINES는 V1 문자열 변환 이후에 prepend — 주입 텍스트가 \bin\b/\bout\b/fragColor
// 치환에 오염되지 않게 함.
const FRAG_V1 =
  DEFINES +
  FRAG.replace('#version 300 es\n', '')
    .replace(/\bin\b/g, 'varying')
    .replace('out vec4 fragColor;\n', '')
    .replace(/fragColor/g, 'gl_FragColor')
    .replace(/\btexture\(/g, 'texture2D(')
    .replace(/discard;/g, 'gl_FragColor = vec4(0.0); return;');

function compileShader(gl: WebGLRenderingContext, type: number, src: string): WebGLShader | null {
  const sh = gl.createShader(type);
  if (!sh) return null;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh);

    console.error(
      `[CyberGridGL] ${type === gl.VERTEX_SHADER ? 'vertex' : 'fragment'} compile failed:\n${log}`
    );
    gl.deleteShader(sh);
    return null;
  }
  return sh;
}

function createProgram(gl: WebGLRenderingContext, vSrc: string, fSrc: string): WebGLProgram | null {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vSrc);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fSrc);
  if (!vs || !fs) return null;
  const prog = gl.createProgram();
  if (!prog) return null;
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.bindAttribLocation(prog, 0, 'aPos');
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(prog);

    console.error(`[CyberGridGL] program link failed:\n${log}`);
    gl.deleteProgram(prog);
    return null;
  }
  // Shaders can be detached after linking
  gl.detachShader(prog, vs);
  gl.detachShader(prog, fs);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  return prog;
}

interface CyberGridGLProps {
  intensity: number;
}

// 모달 활성/진행 값은 hologramSceneStore가 single source.
// 이 컴포넌트는 매 프레임 store를 폴링해서 셰이더 uniform을 갱신함.
export function CyberGridGL({ intensity }: CyberGridGLProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Attempt WebGL2, fall back to WebGL1
    const opts: WebGLContextAttributes = {
      alpha: true,
      premultipliedAlpha: false,
      antialias: false,
      powerPreference: 'default',
    };
    let gl: WebGLRenderingContext | null = canvas.getContext('webgl2', opts);
    let isV2 = !!gl;
    if (!gl) {
      gl = canvas.getContext('webgl', opts);
      isV2 = false;
    }
    if (!gl) return; // No WebGL — parent shows CSS fallback

    const program = createProgram(gl, isV2 ? VERT : VERT_V1, isV2 ? FRAG_V2 : FRAG_V1);
    if (!program) return;

    // Fullscreen quad: 2 triangles
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW
    );

    const uTime = gl.getUniformLocation(program, 'uTime');
    const uGridIntensity = gl.getUniformLocation(program, 'uGridIntensity');
    const uResolution = gl.getUniformLocation(program, 'uResolution');
    const uModalActive = gl.getUniformLocation(program, 'uModalActive');
    const uModalProgress = gl.getUniformLocation(program, 'uModalProgress');
    const uModalScale = gl.getUniformLocation(program, 'uModalScale');
    const uReveal = gl.getUniformLocation(program, 'uReveal');
    const uVeilPanel = gl.getUniformLocation(program, 'uVeilPanel');
    const uVeilPanelA = gl.getUniformLocation(program, 'uVeilPanelA');

    // 베일에 실린 접속 승인 모달 — 비어 있는 1px 로 시작해 store 의 번호가 바뀔 때만 다시 올린다
    const veilTex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, veilTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(4));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    let veilVersion = -1;
    function syncVeilPanel() {
      if (!gl) return;
      if (veilVersion !== hologramSceneStore.veilPanelVersion) {
        veilVersion = hologramSceneStore.veilPanelVersion;
        const panel = hologramSceneStore.veilPanel;
        if (panel) {
          gl.bindTexture(gl.TEXTURE_2D, veilTex);
          gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, panel);
        }
      }
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, veilTex);
      gl.uniform1i(uVeilPanel, 0);
      gl.uniform1f(uVeilPanelA, hologramSceneStore.veilPanel ? 1 : 0);
    }

    gl.useProgram(program);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE); // Additive blending

    // canvas.width 를 새로 쓰면 드로잉 버퍼가 비워진다 — 쉬는 중이어도 한 번은 다시 그린다.
    let forceDraw = false;

    // Resize handling
    function resize() {
      if (!canvas || !gl) return;
      // 1.5배 상한 — 제단 모서리(1~2px 선)가 1배에선 레티나에서 뭉개진다. 2배는 픽셀 4배라
      // 2026-03 DPR 캡(3922dbca8)을 되돌리는 셈이고, 1.5배는 2.25배다.
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      const w = Math.round(canvas.clientWidth * dpr);
      const h = Math.round(canvas.clientHeight * dpr);
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
        forceDraw = true;
      }
    }
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();

    // Reduced motion check
    const motionMq = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Animation loop — adaptive throttle.
    // Why: cyber-grid 배경은 거의 정적이지만 15fps에선 그리드/교차 펄스 앰비언트 모션이
    // 끊겨 보여 30fps로 올림 (60Hz 디스플레이에서 1000/24는 20fps로 양자화되므로 30이 정답).
    // 모달 진입 애니메이션은 더 빠른 위치 이동이라 활성 시 60fps로 승격.
    const IDLE_INTERVAL = 1000 / 30;
    const ACTIVE_INTERVAL = 1000 / 60;
    let raf = 0;
    let startTime = performance.now();
    let lastFrame = 0;
    let currentIntensity = intensity;
    let currentModalActive = 0;
    let currentModalProgress = 0;
    let currentModalScale = 1;
    let stopped = false;
    let paused = false;
    let pausedAt = 0;
    let lastCoverageCheck = -Infinity;

    function frame(now: number) {
      if (stopped || !gl) return;

      // Why: 모달 값은 hologramSceneStore에서 직접 폴링. React state 갱신을 거치지 않으므로
      // 60fps 모달 애니메이션 동안 컴포넌트 트리 리렌더링 비용이 0.
      currentModalActive = hologramSceneStore.modalActive;
      currentModalProgress = hologramSceneStore.modalProgress;
      currentModalScale = hologramSceneStore.modalScale;

      if (motionMq.matches) {
        // Render one static frame, then stop
        const elapsed = (now - startTime) / 1000;
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.useProgram(program);
        gl.uniform1f(uTime, elapsed);
        gl.uniform1f(uGridIntensity, currentIntensity);
        gl.uniform1f(uModalActive, currentModalActive);
        gl.uniform1f(uModalProgress, currentModalProgress);
        gl.uniform1f(uReveal, hologramSceneStore.reveal);
        syncVeilPanel();
        gl.uniform2f(
          uResolution,
          (canvas as HTMLCanvasElement).width,
          (canvas as HTMLCanvasElement).height
        );
        gl.drawArrays(gl.TRIANGLES, 0, 6);
        return;
      }

      raf = requestAnimationFrame(frame);

      // 모달 투사와 렌더 스윕은 늘 60fps 로 그린다 — 그땐 DOM 을 읽지 않고 바로 재개한다.
      const modalActive = currentModalActive > 0.001;
      const animating = modalActive || hologramSceneStore.reveal < 1;
      if (animating || now - lastCoverageCheck >= COVERAGE_CHECK_MS) {
        lastCoverageCheck = now;
        const covered =
          !animating &&
          coveredFraction(
            Array.from(document.querySelectorAll(WINDOW_SURFACE_SELECTOR), (el) =>
              el.getBoundingClientRect()
            ),
            (canvas as HTMLCanvasElement).getBoundingClientRect()
          ) >= PAUSE_COVERAGE;
        if (covered && !paused) pausedAt = now;
        // 쉰 시간만큼 시계를 미뤄 재개 때 격자 흐름·펄스 위상이 튀지 않게 한다.
        if (!covered && paused) startTime += now - pausedAt;
        paused = covered;
      }
      if (paused && !forceDraw) return;

      const interval = animating ? ACTIVE_INTERVAL : IDLE_INTERVAL;
      if (now - lastFrame < interval) return;
      lastFrame = now;
      forceDraw = false;

      // 쉬는 중 강제 프레임은 멈춘 시각으로 그려 재개 때 시계가 뒤로 튀지 않게 한다.
      const elapsed = ((paused ? pausedAt : now) - startTime) / 1000;
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(program);
      gl.uniform1f(uTime, elapsed);
      gl.uniform1f(uGridIntensity, currentIntensity);
      gl.uniform1f(uModalActive, currentModalActive);
      gl.uniform1f(uModalProgress, currentModalProgress);
      gl.uniform1f(uModalScale, currentModalScale);
      gl.uniform1f(uReveal, hologramSceneStore.reveal);
      syncVeilPanel();
      gl.uniform2f(
        uResolution,
        (canvas as HTMLCanvasElement).width,
        (canvas as HTMLCanvasElement).height
      );
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    }

    raf = requestAnimationFrame(frame);

    // Listen for motion preference changes
    function onMotionChange() {
      if (motionMq.matches) {
        cancelAnimationFrame(raf);
      } else {
        startTime = performance.now();
        raf = requestAnimationFrame(frame);
      }
    }
    motionMq.addEventListener('change', onMotionChange);

    // Context loss handling
    function onLost(e: Event) {
      e.preventDefault();
      cancelAnimationFrame(raf);
    }
    function onRestored() {
      startTime = performance.now();
      raf = requestAnimationFrame(frame);
    }
    canvas.addEventListener('webglcontextlost', onLost);
    canvas.addEventListener('webglcontextrestored', onRestored);

    // Expose intensity updater via ref-stable closure
    const updateIntensity = (v: number) => {
      currentIntensity = v;
    };
    (canvas as unknown as { __updateIntensity: typeof updateIntensity }).__updateIntensity =
      updateIntensity;

    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      ro.disconnect();
      motionMq.removeEventListener('change', onMotionChange);
      canvas.removeEventListener('webglcontextlost', onLost);
      canvas.removeEventListener('webglcontextrestored', onRestored);
      gl.deleteBuffer(buf);
      gl.deleteTexture(veilTex);
      gl.deleteProgram(program);
      // GC 를 기다리지 않고 드로잉 버퍼·GPU 자원을 바로 돌려준다. 캔버스가 아직 붙어 있으면
      // StrictMode 의 cleanup→재실행이라 같은 캔버스를 다시 쓰므로 잃게 하지 않는다.
      if (!canvas.isConnected) gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- WebGL 컨텍스트는 마운트 때 한 번만 만든다. intensity 는 시작값으로만 읽고, 이후 변화는 아래 effect 가 __updateIntensity 로 넘긴다
  }, []);

  // Update intensity without re-initializing WebGL
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const updater = (canvas as unknown as { __updateIntensity?: (v: number) => void })
      .__updateIntensity;
    updater?.(intensity);
  }, [intensity]);

  return <canvas ref={canvasRef} className={s.cyberGlCanvas} />;
}
